import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    inject,
} from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { SessionFacade } from "./features/authentication";
import { CallFacade } from "./features/calls";
import { AppInitializationService } from "./core/config/app-initialization.service";

@Component({
    selector: "next-root",
    imports: [RouterOutlet, TranslateModule],
    template: `
        <router-outlet />
        @if (initialization.status() === "failed") {
            <div
                class="startup-curtain startup-error"
                role="alert"
                aria-live="assertive"
            >
                <div class="startup-brand" aria-hidden="true">
                    <span class="material-symbols-rounded">change_history</span>
                    <strong>GEMVISION</strong>
                </div>
                <span
                    class="error-icon material-symbols-rounded"
                    aria-hidden="true"
                    >cloud_off</span
                >
                <div class="error-copy">
                    <h1>{{ "app.startupError.title" | translate }}</h1>
                    <p>
                        {{
                            "app.startupError." + initialization.error()
                                | translate
                        }}
                    </p>
                </div>
                <button type="button" (click)="initialization.retry()">
                    <span class="material-symbols-rounded">refresh</span>
                    {{ "app.startupError.retry" | translate }}
                </button>
                <small>{{ "app.startupError.help" | translate }}</small>
            </div>
        } @else if (session.status() === "workspace-load-failed") {
            <div
                class="startup-curtain startup-error"
                role="alert"
                aria-live="assertive"
            >
                <div class="startup-brand" aria-hidden="true">
                    <span class="material-symbols-rounded">change_history</span>
                    <strong>GEMVISION</strong>
                </div>
                <span
                    class="error-icon material-symbols-rounded"
                    aria-hidden="true"
                    >cloud_off</span
                >
                <div class="error-copy">
                    <h1>{{ "auth.workspaceLoad.title" | translate }}</h1>
                    <p>{{ session.error() }}</p>
                </div>
                <div class="error-actions">
                    <button
                        type="button"
                        (click)="session.retryWorkspaceLoad()"
                    >
                        <span class="material-symbols-rounded">refresh</span>
                        {{ "auth.workspaceLoad.retry" | translate }}
                    </button>
                    <button
                        class="secondary-action"
                        type="button"
                        (click)="session.logout()"
                    >
                        {{ "auth.workspaceLoad.signOut" | translate }}
                    </button>
                </div>
                <small>{{ "auth.workspaceLoad.help" | translate }}</small>
            </div>
        } @else if (showStartupCurtain) {
            <div
                class="startup-curtain"
                role="status"
                aria-live="polite"
                aria-busy="true"
            >
                <div class="startup-brand" aria-hidden="true">
                    <span class="material-symbols-rounded">change_history</span>
                    <strong>GEMVISION</strong>
                </div>
                <span class="spinner" aria-hidden="true"></span>
                <span class="loading-label">{{
                    "app.loading" | translate
                }}</span>
            </div>
        }
    `,
    styleUrl: "./app.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
    protected readonly session = inject(SessionFacade);
    private readonly calls = inject(CallFacade);
    protected readonly initialization = inject(AppInitializationService);

    @HostListener("window:beforeunload")
    @HostListener("window:pagehide")
    protected endActiveCallBeforePageExit(): void {
        this.calls.endForPageExit();
    }

    get showStartupCurtain(): boolean {
        return (
            this.initialization.status() === "loading" ||
            ["unknown", "restoring", "loading-user"].includes(
                this.session.status()
            )
        );
    }
}
